import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Achievement } from '../components/Achievement';
import { BrandMark } from '../components/BrandMark';
import { EventPoster } from '../components/EventPoster';
import { Icon } from '../components/Icon';
import { PrivacyModal } from '../components/PrivacyModal';
import { ProcessingModal, type ProcessingStage } from '../components/ProcessingModal';
import { ReferralInviteModal } from '../components/ReferralInviteModal';
import { RegistrationSuccessExperience } from '../components/RegistrationSuccessExperience';
import { SoundToggle } from '../components/SoundToggle';
import { ApiError, api } from '../lib/api';
import { EVENT_DATE, EVENT_HOURS, EVENT_NAME, EVENT_PRICE, formatCurrency, formatPhoneInput, validBrazilPhone, validEmail, validFullName } from '../lib/format';
import { referralTokenFromLocation, referralWhatsAppUrl } from '../lib/referrals';
import { calculateScrollPercent, getTrackingContext } from '../lib/tracking';
import { useSound } from '../lib/sound';
import { usePageTelemetry } from '../hooks/usePageTelemetry';
import type { RegistrationFormData, RegistrationLookup, RegistrationType } from '../types';

type FormValues = {
  participantName: string;
  participantAge: string;
  participantPhone: string;
  participantEmail: string;
  responsibleName: string;
  responsiblePhone: string;
  attendsForteens: '' | 'yes' | 'no';
  attendsCell: '' | 'yes' | 'no';
  comesFromChurch: '' | 'yes' | 'no';
  churchName: string;
  cellName: string;
  foodRestriction: string;
  foodRestrictionChoice: '' | 'yes' | 'no';
  collaboratorRole: string;
  privacyAccepted: boolean;
};

type FormErrors = Partial<Record<keyof FormValues, string>> & { submit?: string };

const initialValues: FormValues = {
  participantName: '',
  participantAge: '',
  participantPhone: '',
  participantEmail: '',
  responsibleName: '',
  responsiblePhone: '',
  attendsForteens: '',
  attendsCell: '',
  comesFromChurch: '',
  churchName: '',
  cellName: '',
  foodRestriction: '',
  foodRestrictionChoice: '',
  collaboratorRole: '',
  privacyAccepted: false,
};

interface RegistrationPageProps {
  type?: RegistrationType;
}

function HeroOrbit(): JSX.Element {
  return (
    <svg className="hero-orbit" viewBox="0 0 340 340" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id="orbitGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#48cae4" stopOpacity=".34" />
          <stop offset="100%" stopColor="#00b4d8" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="orbitStroke" x1="25" y1="25" x2="315" y2="315">
          <stop stopColor="#48cae4" />
          <stop offset=".5" stopColor="#4a8fe7" stopOpacity=".25" />
          <stop offset="1" stopColor="#00b4d8" />
        </linearGradient>
      </defs>
      <circle cx="170" cy="170" r="166" fill="url(#orbitGlow)" />
      <circle className="hero-orbit__ring" cx="170" cy="170" r="119" stroke="url(#orbitStroke)" strokeWidth="1" strokeDasharray="3 12" />
      <circle className="hero-orbit__ring hero-orbit__ring--reverse" cx="170" cy="170" r="83" stroke="#48cae4" strokeOpacity=".35" strokeWidth="1" strokeDasharray="2 14" />
      <g>
        <circle cx="170" cy="51" r="5" fill="#48cae4">
          <animateTransform attributeName="transform" type="rotate" from="0 170 170" to="360 170 170" dur="13s" repeatCount="indefinite" />
        </circle>
      </g>
      <g>
        <circle cx="170" cy="87" r="3" fill="#00b4d8">
          <animateTransform attributeName="transform" type="rotate" from="360 170 170" to="0 170 170" dur="9s" repeatCount="indefinite" />
        </circle>
      </g>
      <path className="hero-orbit__path" d="M82 198c31 57 104 79 161 48 26-14 43-35 53-59" stroke="#48cae4" strokeOpacity=".55" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Field({
  id,
  label,
  error,
  children,
  hint,
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
  hint?: string;
}): JSX.Element {
  return (
    <label className={`field ${error ? 'field--error' : ''}`} htmlFor={id}>
      <span className="field__label">{label}</span>
      {children}
      {hint ? <span className="field__hint">{hint}</span> : null}
      {error ? <span className="field__error" role="alert">{error}</span> : null}
    </label>
  );
}

export function RegistrationPage({ type = 'participant' }: RegistrationPageProps): JSX.Element {
  const isCollaborator = type === 'collaborator';
  const minAge = 12;
  const maxAge = isCollaborator ? 99 : 17;
  const [values, setValues] = useState<FormValues>(initialValues);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<FormErrors>({});
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [achievement, setAchievement] = useState<{ label: string; detail: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [eventPriceCents, setEventPriceCents] = useState(EVENT_PRICE);
  const [draftRestored, setDraftRestored] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [storedRegistration, setStoredRegistration] = useState<RegistrationLookup | null>(null);
  const [storedRegistrationChecked, setStoredRegistrationChecked] = useState(false);
  const [resumingPayment, setResumingPayment] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [processingOpen, setProcessingOpen] = useState(false);
  const [processingStage, setProcessingStage] = useState<ProcessingStage>('validating');
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [celebrateRegistration, setCelebrateRegistration] = useState(false);
  const [typedTitle, setTypedTitle] = useState('');
  const [inviteName, setInviteName] = useState<string | null>(null);
  const [collaboratorGateOpen, setCollaboratorGateOpen] = useState(isCollaborator);
  const formRef = useRef<HTMLElement>(null);
  const openedAtRef = useRef(Date.now());
  const formStartedRef = useRef(false);
  const { play } = useSound();
  const navigate = useNavigate();
  const track = usePageTelemetry(isCollaborator ? 'collaborator' : 'registration');
  const totalSteps = isCollaborator ? 3 : 4;

  const draftKey = `forteens.registration-draft.${type}`;

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(draftKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { values?: Partial<FormValues>; step?: number };
        if (parsed.values) setValues((current) => ({ ...current, ...parsed.values }));
        if (typeof parsed.step === 'number') setStep(Math.min(Math.max(parsed.step, 0), totalSteps - 1));
        setDraftRestored(Boolean(parsed.values));
      }
    } catch { /* Draft persistence is optional and never blocks registration. */ }
    finally { setDraftReady(true); }
    void api.publicEventConfig().then((config) => setEventPriceCents(config.eventPriceCents)).catch(() => undefined);
  }, [draftKey, totalSteps]);

  useEffect(() => {
    let active = true;
    try {
      const saved = JSON.parse(window.localStorage.getItem('forteens.last-registration') || 'null') as { registrationCode?: string; participantName?: string; registrationType?: RegistrationType; paymentStatus?: RegistrationLookup['paymentStatus'] } | null;
      const code = saved?.registrationCode?.trim().toUpperCase();
      if (!code) { setStoredRegistrationChecked(true); return () => { active = false; }; }
      setStoredRegistration({ id: code, registrationCode: code, registrationType: saved?.registrationType ?? type, participantName: saved?.participantName || 'Inscrição salva', status: 'confirmed', paymentStatus: saved?.paymentStatus ?? 'pending_payment' });
      void api.lookupRegistration({ registrationCode: code }).then((response) => {
        if (active && response.registration) setStoredRegistration(response.registration);
      }).catch(() => undefined).finally(() => { if (active) setStoredRegistrationChecked(true); });
    } catch { setStoredRegistrationChecked(true); }
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (isCollaborator) { setTypedTitle('A madrugada também precisa de quem faz acontecer.'); return; }
    const title = '12 Horas com Forteens';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setTypedTitle(title); return; }
    let cancelled = false; let timer = 0; let index = 0;
    const typeNext = () => {
      if (cancelled) return;
      index += 1; setTypedTitle(title.slice(0, index));
      if (index < title.length) timer = window.setTimeout(typeNext, 82);
      else timer = window.setTimeout(() => { index = 0; setTypedTitle(''); timer = window.setTimeout(typeNext, 240); }, 3000);
    };
    timer = window.setTimeout(typeNext, 180);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [isCollaborator]);

  useEffect(() => {
    const token = referralTokenFromLocation();
    if (!token) return;
    void api.resolveReferral(token).then((invite) => setInviteName(invite?.inviterName ?? null)).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!draftReady) return;
    try { window.localStorage.setItem(draftKey, JSON.stringify({ values, step, savedAt: new Date().toISOString() })); }
    catch { /* Draft persistence is best-effort. */ }
  }, [draftKey, draftReady, step, values]);

  const requiresResponsible = useMemo(() => {
    const age = Number(values.participantAge);
    return !Number.isNaN(age) && age < 18;
  }, [values.participantAge]);

  const pageMeta = isCollaborator
    ? { eyebrow: 'EQUIPE DE OPERAÇÃO', title: 'A madrugada também precisa de quem faz acontecer.', CTA: 'Entrar como colaborador' }
    : { eyebrow: '31 DE JULHO · OAK CHURCH', title: '12 Horas com Forteens', CTA: 'Garantir minha vaga' };

  function setValue<K extends keyof FormValues>(key: K, value: FormValues[K]): void {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      track('form_started');
    }
    setValues((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: undefined, submit: undefined }));
  }

  function validateCurrentStep(): FormErrors {
    const next: FormErrors = {};
    const age = Number(values.participantAge);
    if (step === 0) {
      if (!validFullName(values.participantName)) next.participantName = 'Digite nome e sobrenome.';
      if (!Number.isInteger(age) || age < minAge || age > maxAge) next.participantAge = `A idade deve estar entre ${minAge} e ${maxAge} anos.`;
      if (!validBrazilPhone(values.participantPhone)) next.participantPhone = 'Digite um celular brasileiro válido.';
      if (!validEmail(values.participantEmail)) next.participantEmail = 'Digite um e-mail válido.';
    }
    if (step === 1 && (isCollaborator ? requiresResponsible : true)) {
      if (!validFullName(values.responsibleName)) next.responsibleName = 'Digite nome e sobrenome do responsável.';
      if (!validBrazilPhone(values.responsiblePhone)) next.responsiblePhone = 'Digite um celular brasileiro válido.';
    }
    const communityStep = isCollaborator ? -1 : 2;
    if (step === communityStep) {
      if (values.attendsForteens === 'yes' && !values.attendsCell) next.attendsCell = 'Selecione uma opção.';
      if (values.attendsForteens === 'yes' && values.attendsCell === 'yes' && values.cellName.trim().length < 2) next.cellName = 'Conte para a gente qual célula.';
      if (!values.attendsForteens) next.attendsForteens = 'Selecione uma opção.';
      if (values.attendsForteens === 'no' && !values.comesFromChurch) next.comesFromChurch = 'Selecione uma opção.';
      if (values.attendsForteens === 'no' && values.comesFromChurch === 'yes' && values.churchName.trim().length < 2) {
        next.churchName = 'Conte para a gente qual igreja.';
      }
    }
    const privacyStep = totalSteps - 1;
    if (step === privacyStep && values.foodRestrictionChoice === 'yes' && values.foodRestriction.trim().length === 0) next.foodRestriction = 'Descreva a restrição em até 50 caracteres.';
    if (step === privacyStep && !values.foodRestrictionChoice) next.foodRestrictionChoice = 'Selecione Não ou informe uma restrição.';
    if (step === privacyStep && !values.privacyAccepted) next.privacyAccepted = 'Você precisa aceitar a Política de Privacidade.';
    return next;
  }

  function revealAchievement(label: string, detail: string): void {
    setAchievement({ label, detail });
    window.setTimeout(() => setAchievement(null), 2200);
  }

  function handleAdvance(): void {
    const nextErrors = validateCurrentStep();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      play('error');
      return;
    }
    if (step === totalSteps - 1) {
      void submit();
      return;
    }
    play('advance');
    track('form_step_completed');
    revealAchievement('Etapa concluída', step === 0 ? 'Sua vaga já tem identidade.' : 'Estamos quase lá.');
    setStep((current) => current + 1);
    window.setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  async function submit(): Promise<void> {
    const nextErrors = validateCurrentStep();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      play('error');
      return;
    }
    setSubmitting(true);
    setProcessingStage('validating');
    setProcessingError(null);
    setProcessingOpen(true);
    try {
      const payload: RegistrationFormData = {
        registrationType: type,
        participantName: values.participantName.trim(),
        participantAge: Number(values.participantAge),
        participantPhone: values.participantPhone,
        participantEmail: values.participantEmail.trim(),
        responsibleName: requiresResponsible || !isCollaborator ? values.responsibleName.trim() : undefined,
        responsiblePhone: requiresResponsible || !isCollaborator ? values.responsiblePhone : undefined,
        attendsForteens: isCollaborator ? undefined : values.attendsForteens === 'yes',
        attendsCell: isCollaborator || values.attendsForteens !== 'yes' ? undefined : values.attendsCell === 'yes',
        comesFromChurch: isCollaborator || values.attendsForteens !== 'no' ? undefined : values.comesFromChurch === 'yes',
        churchName: isCollaborator || values.attendsForteens !== 'no' || values.comesFromChurch !== 'yes' ? undefined : values.churchName.trim(),
        cellName: isCollaborator || values.attendsForteens !== 'yes' || values.attendsCell !== 'yes' ? undefined : values.cellName.trim(),
        foodRestriction: values.foodRestrictionChoice === 'yes' ? values.foodRestriction.trim() : undefined,
        collaboratorRole: isCollaborator ? values.collaboratorRole.trim() || undefined : undefined,
        privacyAccepted: true,
        tracking: getTrackingContext({
          timeOnPageSeconds: Math.round((Date.now() - openedAtRef.current) / 1000),
          scrollPercent: calculateScrollPercent(),
        }),
      };
      // Keep the first validation frame visible long enough to avoid a modal
      // flash on fast connections, without delaying the network request.
      const registrationRequest = api.createRegistration(payload);
      await new Promise((resolve) => window.setTimeout(resolve, 180));
      setProcessingStage('creating_pass');
      const result = await registrationRequest;
      setProcessingStage('preparing_payment');
      const savedRegistration = { ...payload, registrationCode: result.registration.registrationCode, paymentStatus: result.registration.paymentStatus, savedAt: new Date().toISOString() };
      try {
        window.localStorage.setItem('forteens.last-registration', JSON.stringify(savedRegistration));
        window.localStorage.removeItem(draftKey);
      } catch { /* Device persistence is optional. */ }
      track('registration_created', result.registration.registrationCode);
      setCheckoutUrl(result.checkoutUrl ?? null);
      const fallback: RegistrationLookup = {
        id: result.registration.registrationCode,
        registrationCode: result.registration.registrationCode,
        registrationType: type,
        participantName: payload.participantName,
        status: result.registration.status,
        paymentStatus: result.registration.paymentStatus,
        qrToken: result.registration.qrToken,
      };
      setStoredRegistration(fallback);
      setStoredRegistrationChecked(true);
      setCelebrateRegistration(!result.duplicate && !result.alreadyRegistered);
      try {
        const response = await api.lookupRegistration({ registrationCode: result.registration.registrationCode });
        if (response.registration) setStoredRegistration(response.registration);
      } catch { /* The creation response already contains the signed QR token. */ }
      if (result.checkoutUrl) {
        try { window.sessionStorage.setItem('forteens.pending-registration-code', result.registration.registrationCode); } catch { /* Return lookup still works by code. */ }
        play('success');
        track('payment_ready', result.registration.registrationCode);
        setProcessingOpen(false);
        return;
      }
      play('success');
      revealAchievement('Inscrição confirmada', 'Seu passe e QR Code já estão guardados.');
      setProcessingOpen(false);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Não foi possível concluir a inscrição. Tente novamente.';
      setErrors({ submit: message });
      setProcessingError(message);
      play('error');
    } finally {
      setSubmitting(false);
    }
  }

  async function resumePayment(): Promise<void> {
    if (!storedRegistration) return;
    setResumingPayment(true);
    try {
      const targetUrl = checkoutUrl || (await api.createPaymentCheckout(storedRegistration.registrationCode)).checkoutUrl;
      try { window.sessionStorage.setItem('forteens.pending-registration-code', storedRegistration.registrationCode); } catch { /* Optional helper. */ }
      track('payment_redirect', storedRegistration.registrationCode);
      window.location.assign(targetUrl);
    } catch (error) {
      setErrors({ submit: error instanceof ApiError ? error.message : 'Não foi possível abrir o pagamento agora.' });
      play('error');
    } finally { setResumingPayment(false); }
  }

  function startNewRegistration(): void {
    try { window.localStorage.removeItem('forteens.last-registration'); window.localStorage.removeItem(draftKey); } catch { /* Device storage is optional. */ }
    setStoredRegistration(null);
    setCheckoutUrl(null);
    setCelebrateRegistration(false);
    setValues(initialValues);
    setStep(0);
    setErrors({});
  }

  function choose(option: 'attendsForteens' | 'attendsCell' | 'comesFromChurch', value: 'yes' | 'no'): void {
    setValue(option, value);
    if (option === 'attendsForteens' && value === 'yes') {
      setValue('comesFromChurch', '');
      setValue('churchName', '');
      setValue('attendsCell', '');
      setValue('cellName', '');
    }
    if (option === 'comesFromChurch' && value === 'no') setValue('churchName', '');
    if (option === 'attendsCell' && value === 'no') setValue('cellName', '');
  }

  function share(referralToken?: string): void {
    track('share_click');
    play('click');
    window.open(referralWhatsAppUrl(referralToken), '_blank', 'noopener,noreferrer');
  }

  function openInstagram(): void {
    const url = import.meta.env.VITE_INSTAGRAM_URL || 'https://www.instagram.com/forteens.oakchurch/';
    track('instagram_click');
    play('click');
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  if (isCollaborator && collaboratorGateOpen) {
    return <main className="public-page collaborator-gate-page"><SoundToggle /><section className="collaborator-gate" role="dialog" aria-modal="true" aria-labelledby="collaborator-gate-title"><span className="eyebrow eyebrow--glow">ACESSO DE OPERAÇÃO</span><BrandMark /><h1 id="collaborator-gate-title">Você é realmente um colaborador do Forteens?</h1><p>Esta página é exclusiva para quem já faz parte da equipe que serve no evento.</p><div><button type="button" className="button button--primary" onClick={() => { setCollaboratorGateOpen(false); play('success'); }}>Sim, sou colaborador<Icon name="check" /></button><button type="button" className="button button--ghost" onClick={() => navigate('/inscricao')}>Não, quero me inscrever<Icon name="arrow-right" /></button></div></section></main>;
  }

  if (storedRegistrationChecked && storedRegistration) {
    return (
      <main className="public-page registration-access-page">
        <SoundToggle />
        <section className="public-container registration-access registration-access--confirmed">
          <BrandMark />
          <RegistrationSuccessExperience
            registrationCode={storedRegistration.registrationCode}
            qrToken={storedRegistration.qrToken}
            participantName={storedRegistration.participantName}
            paymentStatus={storedRegistration.paymentStatus}
            team={storedRegistration.team}
            celebrate={celebrateRegistration}
            paymentBusy={resumingPayment}
            onContinuePayment={() => void resumePayment()}
            onShare={() => share(storedRegistration.referralToken)}
            onNewRegistration={startNewRegistration}
          />
          {errors.submit ? <p className="form-alert" role="alert"><Icon name="warning" />{errors.submit}</p> : null}
        </section>
        {inviteName ? <ReferralInviteModal inviterName={inviteName} onClose={() => setInviteName(null)} /> : null}
      </main>
    );
  }

  const communityStep = isCollaborator ? -1 : 2;
  const privacyStep = totalSteps - 1;

  return (
    <main className="public-page">
      <SoundToggle />
      <section className="event-hero">
        <HeroOrbit />
        <div className="event-hero__grain" />
        <div className="public-container event-hero__content">
          <div className="event-hero__copy">
            <BrandMark />
            <p className="eyebrow eyebrow--glow">{pageMeta.eyebrow}</p>
            <h1 className={!isCollaborator ? 'typing-title' : undefined}>{isCollaborator ? pageMeta.title : typedTitle}{!isCollaborator ? <span className="typing-title__cursor" aria-hidden="true" /> : null}</h1>
            <p className="event-hero__lede">
              {isCollaborator
                ? 'Inscreva-se para servir com propósito em uma experiência intensa, segura e inesquecível.'
                : 'Será uma noite memorável na presença de Deus.'}
            </p>
            <div className="event-facts" aria-label="Informações do evento">
              <span><Icon name="calendar" /> {EVENT_DATE}</span>
              <span><Icon name="clock" /> {EVENT_HOURS}</span>
              <span><strong>{formatCurrency(eventPriceCents)}</strong> inscrição</span>
            </div>
            <button type="button" className="button button--primary button--hero" onClick={() => {
              play('click');
              formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}>
              {pageMeta.CTA}<Icon name="arrow-right" />
            </button>
            {!isCollaborator ? (
              <button type="button" className="text-action text-action--hero" onClick={() => share()}>
                <Icon name="share" /> Chamar a galera no WhatsApp
              </button>
            ) : null}
          </div>
          {!isCollaborator ? <EventPoster className="event-hero__poster" eager compact /> : null}
        </div>
      </section>

      <section className="public-container registration-stage" ref={formRef} aria-label="Formulário de inscrição">
        <div className="registration-stage__intro">
          <span className="eyebrow">SEU PASSE PARA A MADRUGADA</span>
          <h2>{isCollaborator ? 'Vamos conhecer sua disponibilidade.' : 'Sua vaga começa aqui.'}</h2>
          <p>Leva menos de dois minutos. Seu progresso fica salvo se você sair.</p>
          {draftRestored ? <span className="draft-recovered"><Icon name="refresh" /> Progresso recuperado</span> : null}
        </div>
        <div className="progress-track" aria-label={`Etapa ${step + 1} de ${totalSteps}`}>
          <div className="progress-track__labels"><span>Etapa {step + 1}</span><strong>{totalSteps} etapas</strong></div>
          <div className="progress-track__rail"><span style={{ width: `${((step + 1) / totalSteps) * 100}%` }} /></div>
          <div className="progress-track__nodes" aria-hidden="true">
            {Array.from({ length: totalSteps }).map((_, index) => <i className={index <= step ? 'is-active' : ''} key={index}>{index < step ? <Icon name="check" /> : index + 1}</i>)}
          </div>
        </div>

        <div className="form-card">
            {step === 0 ? (
              <div className="form-step form-step--active">
                <span className="step-kicker">01 · SOBRE VOCÊ</span>
                <h3>{isCollaborator ? 'Sua identidade na operação' : 'Faça sua inscrição aqui'}</h3>
                <div className="field-grid">
                  <Field id="participantName" label="Nome completo" error={errors.participantName}>
                    <input id="participantName" autoComplete="name" value={values.participantName} onChange={(event) => setValue('participantName', event.target.value)} placeholder="Nome e sobrenome" />
                  </Field>
                  <Field id="participantAge" label="Idade" error={errors.participantAge}>
                    <select id="participantAge" value={values.participantAge} onChange={(event) => setValue('participantAge', event.target.value)}>
                      <option value="">Selecione</option>
                      {Array.from({ length: maxAge - minAge + 1 }, (_, index) => minAge + index).map((ageOption) => <option value={ageOption} key={ageOption}>{ageOption} anos</option>)}
                    </select>
                  </Field>
                  <Field id="participantPhone" label="Celular com WhatsApp" error={errors.participantPhone}>
                    <input id="participantPhone" autoComplete="tel" inputMode="tel" maxLength={13} value={values.participantPhone} onChange={(event) => setValue('participantPhone', formatPhoneInput(event.target.value))} placeholder="DDD + celular · 11 dígitos" />
                  </Field>
                  <Field id="participantEmail" label="E-mail" error={errors.participantEmail}>
                    <input id="participantEmail" autoComplete="email" inputMode="email" value={values.participantEmail} onChange={(event) => setValue('participantEmail', event.target.value)} placeholder="voce@email.com" />
                  </Field>
                </div>
              </div>
            ) : null}

            {step === 1 && (isCollaborator ? requiresResponsible : true) ? (
              <div className="form-step form-step--active">
                <span className="step-kicker">02 · RESPONSÁVEL</span>
                <h3>{isCollaborator ? 'Por você ainda ser menor, precisamos do responsável.' : 'Agora, quem cuida de você.'}</h3>
                <p className="form-step__caption">Usaremos este contato somente para informações importantes sobre o evento.</p>
                <div className="field-grid">
                  <Field id="responsibleName" label="Nome completo do responsável" error={errors.responsibleName}>
                    <input id="responsibleName" autoComplete="name" value={values.responsibleName} onChange={(event) => setValue('responsibleName', event.target.value)} placeholder="Nome e sobrenome" />
                  </Field>
                  <Field id="responsiblePhone" label="Celular do responsável" error={errors.responsiblePhone}>
                    <input id="responsiblePhone" autoComplete="tel" inputMode="tel" value={values.responsiblePhone} onChange={(event) => setValue('responsiblePhone', event.target.value)} placeholder="(00) 00000-0000" />
                  </Field>
                </div>
              </div>
            ) : null}

            {step === 1 && isCollaborator && !requiresResponsible ? (
              <div className="form-step form-step--active">
                <span className="step-kicker">02 · COMO VOCÊ CHEGA</span>
                <h3>De qual departamento você já faz parte?</h3>
                <p className="form-step__caption">Conte a função, área ou time em que você já serve no Forteens.</p>
                <Field id="collaboratorRole" label="Departamento, função ou área" hint="Ex.: recepção, mídia, intercessão, apoio">
                  <input id="collaboratorRole" value={values.collaboratorRole} onChange={(event) => setValue('collaboratorRole', event.target.value)} placeholder="Ex.: Mídia, recepção, intercessão" />
                </Field>
              </div>
            ) : null}

            {step === communityStep ? (
              <div className="form-step form-step--active">
                <span className="step-kicker">03 · A SUA GALERA</span>
                <h3>Você já faz parte do Forteens?</h3>
                <div className="choice-row" role="group" aria-label="Frequenta o Forteens?">
                  <button type="button" className={`choice-card ${values.attendsForteens === 'yes' ? 'is-selected' : ''}`} onClick={() => choose('attendsForteens', 'yes')}><Icon name="check" /> Sim, tô em casa</button>
              <button type="button" className={`choice-card ${values.attendsForteens === 'no' ? 'is-selected' : ''}`} onClick={() => choose('attendsForteens', 'no')}><Icon name="spark" /> Ainda não</button>
                </div>
                {errors.attendsForteens ? <span className="field__error" role="alert">{errors.attendsForteens}</span> : null}
                {values.attendsForteens === 'yes' ? (
                  <div className="conditional-fields">
                    <h4>Você já frequenta uma célula?</h4>
                    <div className="choice-row choice-row--small" role="group" aria-label="Frequenta uma célula?">
                      <button type="button" className={`choice-card ${values.attendsCell === 'yes' ? 'is-selected' : ''}`} onClick={() => choose('attendsCell', 'yes')}>Sim</button>
                      <button type="button" className={`choice-card ${values.attendsCell === 'no' ? 'is-selected' : ''}`} onClick={() => choose('attendsCell', 'no')}>Não</button>
                    </div>
                    {errors.attendsCell ? <span className="field__error" role="alert">{errors.attendsCell}</span> : null}
                    {values.attendsCell === 'yes' ? <Field id="cellName" label="Qual célula?" error={errors.cellName}><input id="cellName" value={values.cellName} onChange={(event) => setValue('cellName', event.target.value)} placeholder="Nome da célula" /></Field> : null}
                  </div>
                ) : null}
                {values.attendsForteens === 'no' ? (
                  <div className="conditional-fields">
                    <h4>Você vem de alguma igreja?</h4>
                    <div className="choice-row choice-row--small" role="group" aria-label="Vem de alguma igreja?">
                      <button type="button" className={`choice-card ${values.comesFromChurch === 'yes' ? 'is-selected' : ''}`} onClick={() => choose('comesFromChurch', 'yes')}>Sim</button>
                      <button type="button" className={`choice-card ${values.comesFromChurch === 'no' ? 'is-selected' : ''}`} onClick={() => choose('comesFromChurch', 'no')}>Não</button>
                    </div>
                    {errors.comesFromChurch ? <span className="field__error" role="alert">{errors.comesFromChurch}</span> : null}
                    {values.comesFromChurch === 'yes' ? (
                      <Field id="churchName" label="Qual igreja?" error={errors.churchName}>
                        <input id="churchName" value={values.churchName} onChange={(event) => setValue('churchName', event.target.value)} placeholder="Nome da igreja" />
                      </Field>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            {step === privacyStep ? (
              <div className="form-step form-step--active">
                <span className="step-kicker">{String(totalSteps).padStart(2, '0')} · PRONTO PARA A VIRADA</span>
                <h3>{isCollaborator ? 'Confirme sua inscrição.' : 'Seu passe está quase garantido.'}</h3>
                <div className="payment-preview">
                  <span><Icon name="shield" /></span>
                  <div><strong>{EVENT_NAME}</strong><small>{EVENT_DATE} · {EVENT_HOURS}</small></div>
                  <b>{formatCurrency(eventPriceCents)}</b>
                </div>
                <div className="food-question">
                  <span className="field__label">Possui alguma restrição alimentar?</span>
                  <div className="choice-row choice-row--small" role="group" aria-label="Possui alguma restrição alimentar?">
                    <button type="button" className={`choice-card ${values.foodRestrictionChoice === 'no' ? 'is-selected' : ''}`} onClick={() => { setValue('foodRestrictionChoice', 'no'); setValue('foodRestriction', ''); }}>Não</button>
                    <button type="button" className={`choice-card ${values.foodRestrictionChoice === 'yes' ? 'is-selected' : ''}`} onClick={() => setValue('foodRestrictionChoice', 'yes')}>Sim</button>
                  </div>
                  {values.foodRestrictionChoice === 'yes' ? <Field id="foodRestriction" label="Qual restrição?" error={errors.foodRestriction} hint={`${values.foodRestriction.length}/50`}><input id="foodRestriction" maxLength={50} value={values.foodRestriction} onChange={(event) => setValue('foodRestriction', event.target.value.slice(0, 50))} placeholder="Ex.: lactose, nozes, glúten" /></Field> : null}
                  {errors.foodRestrictionChoice ? <span className="field__error" role="alert">{errors.foodRestrictionChoice}</span> : null}
                </div>
                <label className={`consent ${errors.privacyAccepted ? 'consent--error' : ''}`}>
                  <input type="checkbox" checked={values.privacyAccepted} onChange={(event) => setValue('privacyAccepted', event.target.checked)} />
                  <span>Ao fazer a inscrição, aceito a <button type="button" onClick={() => setPrivacyOpen(true)}>Política de Privacidade</button>.</span>
                </label>
                {errors.privacyAccepted ? <span className="field__error" role="alert">{errors.privacyAccepted}</span> : null}
                <p className="secure-note"><Icon name="lock" /> O pagamento é concluído em ambiente seguro da InfinitePay.</p>
              </div>
            ) : null}

            {errors.submit ? <div className="form-alert" role="alert"><Icon name="warning" />{errors.submit}</div> : null}
            <div className="form-card__actions">
              {step > 0 ? <button type="button" className="button button--ghost" onClick={() => { play('click'); setStep((current) => current - 1); }}>Voltar</button> : <span />}
              <button type="button" className="button button--primary" disabled={submitting} onClick={handleAdvance}>
                {submitting ? 'Preparando…' : step === privacyStep ? 'Confirmar inscrição' : 'Continuar'}
                {!submitting ? <Icon name="arrow-right" /> : null}
              </button>
            </div>
        </div>
      </section>

      {!isCollaborator ? (
        <section className="public-container public-cta-strip">
          <div><span className="eyebrow">POR TRÁS DA EXPERIÊNCIA</span><h2>Acompanhe a contagem regressiva.</h2></div>
          <button type="button" className="instagram-cta" onClick={openInstagram}><Icon name="instagram" /> Acompanhe os bastidores no Instagram <Icon name="external" /></button>
        </section>
      ) : null}
      <footer className="public-footer">12 HORAS COM FORTEENS · {EVENT_DATE} · OAK CHURCH</footer>
      {achievement ? <Achievement {...achievement} /> : null}
      {inviteName ? <ReferralInviteModal inviterName={inviteName} onClose={() => setInviteName(null)} /> : null}
      <PrivacyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      <ProcessingModal
        open={processingOpen}
        stage={processingStage}
        errorMessage={processingError}
        onRetry={processingError ? () => void submit() : undefined}
        onDismiss={processingError ? () => { setProcessingOpen(false); setProcessingError(null); } : undefined}
      />
    </main>
  );
}
